import React, { memo, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Mail,
  Phone,
  Instagram,
  Facebook,
  Youtube,
  Twitter,
  MapPin,
  Clock,
  ArrowRight,
  CheckCircle,
} from "lucide-react";
import { api } from "../context/AppContext";
import toast from "react-hot-toast";

const DEFAULT_INFO = {
  address: "10/11, 3rd Main, 3rd Cross, Kempapura, Bengaluru 560024",
  phone: "+91 98765 43210",
  email: "info@proteinspot.in",
  hours: "Every day, 8 am – 9 pm",
  mapLabel: "Hebbal, Bengaluru",
  mapEmbed: "",
};

const NAV = {
  Menu: [
    ["Fresh Juices", "/products?section=juices"],
    ["Salads", "/products?section=salads"],
    ["Sandwiches", "/products?section=sandwiches"],
    ["Smoothies", "/products?section=smoothies"],
    ["Bestsellers", "/products?bestseller=true"],
    ["Today's Specials", "/products?featured=true"],
  ],
  Account: [
    ["My Orders", "/orders"],
    ["My Cart", "/cart"],
    ["My Profile", "/profile"],
  ],
  Legal: [
    ["Privacy Policy", "/"],
    ["Terms of Service", "/"],
    ["Refund Policy", "/"],
  ],
};

const TRUST = [
  { icon: "🌿", label: "100% Natural" },
  { icon: "🚫", label: "No Preservatives" },
  { icon: "⚡", label: "Zero Added Sugar" },
  { icon: "✅", label: "FSSAI Certified" },
  { icon: "🔒", label: "Secure Payments" },
  { icon: "🚚", label: "2-Hour Delivery" },
];

const SOCIALS = [
  {
    Icon: Instagram,
    label: "Instagram",
    href: "https://instagram.com/",
  },
  {
    Icon: Facebook,
    label: "Facebook",
    href: "https://facebook.com/",
  },
  { Icon: Twitter, label: "Twitter", href: "https://twitter.com/" },
  { Icon: Youtube, label: "YouTube", href: "https://youtube.com/" },
];

const DIM = "rgba(209,250,229,0.45)";

const FLink = ({ to, children }) => (
  <Link
    to={to}
    style={{
      color: DIM,
      fontSize: 12.5,
      textDecoration: "none",
      fontWeight: 500,
      transition: "color 0.15s",
    }}
    onMouseEnter={(e) => (e.currentTarget.style.color = "#fbbf24")}
    onMouseLeave={(e) => (e.currentTarget.style.color = DIM)}
  >
    {children}
  </Link>
);

const ColHead = ({ children }) => (
  <h4
    style={{
      color: "#fff",
      fontWeight: 700,
      fontSize: 10,
      letterSpacing: "2px",
      textTransform: "uppercase",
      margin: "0 0 12px",
    }}
  >
    {children}
  </h4>
);

const Footer = () => {
  const year = new Date().getFullYear();
  const [email, setEmail] = useState("");
  const [subbed, setSubbed] = useState(false);
  const [info, setInfo] = useState(DEFAULT_INFO);

  useEffect(() => {
    api
      .get("/contact/info")
      .then((res) => {
        if (res.data) setInfo({ ...DEFAULT_INFO, ...res.data });
      })
      .catch(() => {});
  }, []);

  const handleSubscribe = () => {
    if (!email.trim() || !email.includes("@")) {
      toast.error("Enter a valid email");
      return;
    }
    setSubbed(true);
    setEmail("");
    toast.success("You're on the list! 🎉");
  };

  return (
    <footer
      style={{
        position: "relative",
        background: "#062c1b",
        color: "#fff",
        overflow: "hidden",
        fontFamily: "'Roboto',sans-serif",
      }}
    >
      {/* bg blobs */}
      <div
        style={{
          position: "absolute",
          top: -60,
          right: -60,
          width: 300,
          height: 300,
          borderRadius: "50%",
          background: "rgba(74,222,128,0.05)",
          filter: "blur(60px)",
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "absolute",
          bottom: -40,
          left: -40,
          width: 200,
          height: 200,
          borderRadius: "50%",
          background: "rgba(251,191,36,0.04)",
          filter: "blur(50px)",
          pointerEvents: "none",
        }}
      />

      {/* ── trust strip ── */}
      <div
        style={{
          borderBottom: "1px solid rgba(255,255,255,0.06)",
          overflowX: "auto",
          scrollbarWidth: "none",
        }}
      >
        <div
          style={{
            maxWidth: 1152,
            margin: "0 auto",
            padding: "0 20px",
            display: "flex",
            minWidth: "max-content",
          }}
        >
          {TRUST.map((t, i) => (
            <div
              key={t.label}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "9px 16px",
                borderRight:
                  i < TRUST.length - 1
                    ? "1px solid rgba(255,255,255,0.05)"
                    : "none",
                whiteSpace: "nowrap",
              }}
            >
              <span style={{ fontSize: 12 }}>{t.icon}</span>
              <span
                style={{
                  fontSize: 10.5,
                  fontWeight: 600,
                  color: "rgba(209,250,229,0.5)",
                }}
              >
                {t.label}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* ── main grid ── */}
      <div
        style={{
          maxWidth: 1152,
          margin: "0 auto",
          padding: "24px 20px 16px",
          position: "relative",
          zIndex: 1,
          boxSizing: "border-box",
        }}
      >
        <div className="ft-grid">
          {/* col 1 — brand */}
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 9,
                marginBottom: 9,
              }}
            >
              <img
                src="https://res.cloudinary.com/proteinspot/image/upload/v1774009667/logo-transparent_tsfxmf.png"
                alt="ProteinSpot"
                style={{
                  width: 34,
                  height: 34,
                  objectFit: "contain",
                  flexShrink: 0,
                }}
              />
              <div style={{ lineHeight: 1.2 }}>
                <div
                  style={{
                    fontWeight: 900,
                    fontSize: 15,
                    letterSpacing: "-0.3px",
                  }}
                >
                  Protein<span style={{ color: "#fbbf24" }}>Spot</span>
                </div>
                <div
                  style={{
                    fontSize: 8,
                    color: "rgba(255,255,255,0.28)",
                    letterSpacing: "1.5px",
                    textTransform: "uppercase",
                    fontWeight: 600,
                  }}
                >
                  Fresh &amp; Natural
                </div>
              </div>
            </div>

            <p
              style={{
                color: "rgba(209,250,229,0.43)",
                fontSize: 11.5,
                lineHeight: 1.65,
                margin: "0 0 10px",
                maxWidth: 250,
              }}
            >
              Cold- juices, salads, sandwiches and smoothies — made
              daily, zero preservatives, 2-hour delivery.
            </p>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 5,
                marginBottom: 12,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 7,
                  color: "rgba(209,250,229,0.55)",
                  fontSize: 11.5,
                }}
              >
                <Clock
                  size={11}
                  color="#fbbf24"
                  style={{ flexShrink: 0, marginTop: 1 }}
                />
                <span>{info.hours}</span>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 7,
                  color: "rgba(209,250,229,0.55)",
                  fontSize: 11.5,
                }}
              >
                <MapPin
                  size={11}
                  color="#fbbf24"
                  style={{ flexShrink: 0, marginTop: 1 }}
                />
                <span>{info.address}</span>
              </div>
            </div>

            <div style={{ display: "flex", gap: 6 }}>
              {SOCIALS.map(({ Icon, label, href }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={label}
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 7,
                    background: "rgba(255,255,255,0.06)",
                    border: "1px solid rgba(255,255,255,0.06)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "rgba(255,255,255,0.5)",
                    textDecoration: "none",
                    transition: "all 0.18s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "#fbbf24";
                    e.currentTarget.style.color = "#1a3a2a";
                    e.currentTarget.style.borderColor = "transparent";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "rgba(255,255,255,0.06)";
                    e.currentTarget.style.color = "rgba(255,255,255,0.5)";
                    e.currentTarget.style.borderColor =
                      "rgba(255,255,255,0.06)";
                  }}
                >
                  <Icon size={12} />
                </a>
              ))}
            </div>
          </div>

          {/* col 2 — menu */}
          <div>
            <ColHead>Menu</ColHead>
            <nav style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {NAV.Menu.map(([l, p]) => (
                <FLink key={l} to={p}>
                  {l}
                </FLink>
              ))}
            </nav>
          </div>

          {/* col 3 — account */}
          <div>
            <ColHead>Account</ColHead>
            <nav style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {NAV.Account.map(([l, p]) => (
                <FLink key={l} to={p}>
                  {l}
                </FLink>
              ))}
            </nav>
          </div>

          {/* col 4 — contact + newsletter */}
          <div>
            <ColHead>Get in Touch</ColHead>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 7,
                marginBottom: 14,
              }}
            >
              <a
                href={`tel:${info.phone}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  textDecoration: "none",
                  color: "rgba(209,250,229,0.55)",
                  fontSize: 12,
                  transition: "color 0.15s",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.color = "#fff")}
                onMouseLeave={(e) =>
                  (e.currentTarget.style.color = "rgba(209,250,229,0.55)")
                }
              >
                <div
                  style={{
                    width: 26,
                    height: 26,
                    borderRadius: 7,
                    background: "rgba(255,255,255,0.06)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Phone size={11} color="#fbbf24" />
                </div>
                {info.phone}
              </a>

              <a
                href={`mailto:${info.email}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  textDecoration: "none",
                  color: "rgba(209,250,229,0.55)",
                  fontSize: 12,
                  transition: "color 0.15s",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.color = "#fff")}
                onMouseLeave={(e) =>
                  (e.currentTarget.style.color = "rgba(209,250,229,0.55)")
                }
              >
                <div
                  style={{
                    width: 26,
                    height: 26,
                    borderRadius: 7,
                    background: "rgba(255,255,255,0.06)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Mail size={11} color="#fbbf24" />
                </div>
                {info.email}
              </a>

              <Link
                to="/contact"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  textDecoration: "none",
                  color: "rgba(209,250,229,0.55)",
                  fontSize: 12,
                  transition: "color 0.15s",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.color = "#fff")}
                onMouseLeave={(e) =>
                  (e.currentTarget.style.color = "rgba(209,250,229,0.55)")
                }
              >
                <div
                  style={{
                    width: 26,
                    height: 26,
                    borderRadius: 7,
                    background: "rgba(255,255,255,0.06)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <MapPin size={11} color="#fbbf24" />
                </div>
                Visit our store
              </Link>
            </div>

            {/* newsletter */}
            <div
              style={{
                background: "rgba(255,255,255,0.04)",
                borderRadius: 11,
                padding: "11px 12px",
                border: "1px solid rgba(255,255,255,0.07)",
              }}
            >
              <p
                style={{
                  color: "#fbbf24",
                  fontWeight: 700,
                  fontSize: 9.5,
                  letterSpacing: "1.2px",
                  textTransform: "uppercase",
                  margin: "0 0 2px",
                }}
              >
                Fresh Deals to Your Inbox
              </p>
              <p
                style={{
                  color: "rgba(209,250,229,0.33)",
                  fontSize: 10.5,
                  margin: "0 0 9px",
                  lineHeight: 1.5,
                }}
              >
                Exclusive offers &amp; seasonal specials.
              </p>
              {subbed ? (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    color: "#4ade80",
                    fontSize: 11.5,
                    fontWeight: 600,
                  }}
                >
                  <CheckCircle size={12} /> You're subscribed!
                </div>
              ) : (
                <div style={{ position: "relative" }}>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSubscribe()}
                    placeholder="your@email.com"
                    style={{
                      width: "100%",
                      padding: "8px 36px 8px 11px",
                      background: "rgba(255,255,255,0.05)",
                      border: "1px solid rgba(255,255,255,0.1)",
                      borderRadius: 100,
                      color: "#fff",
                      fontSize: 11.5,
                      outline: "none",
                      boxSizing: "border-box",
                      fontFamily: "inherit",
                      transition: "border-color 0.2s",
                    }}
                    onFocus={(e) =>
                      (e.target.style.borderColor = "rgba(251,191,36,0.5)")
                    }
                    onBlur={(e) =>
                      (e.target.style.borderColor = "rgba(255,255,255,0.1)")
                    }
                  />
                  <button
                    onClick={handleSubscribe}
                    style={{
                      position: "absolute",
                      right: 4,
                      top: "50%",
                      transform: "translateY(-50%)",
                      width: 24,
                      height: 24,
                      borderRadius: "50%",
                      border: "none",
                      background: "#fbbf24",
                      color: "#1a3a2a",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transition: "background 0.15s",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = "#fcd34d")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = "#fbbf24")
                    }
                  >
                    <ArrowRight size={11} strokeWidth={2.5} />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── bottom bar ── */}
        <div
          style={{
            borderTop: "1px solid rgba(255,255,255,0.06)",
            marginTop: 18,
            paddingTop: 12,
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 8,
          }}
        >
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            {NAV.Legal.map(([l, p]) => (
              <Link
                key={l}
                to={p}
                style={{
                  fontSize: 10,
                  color: "rgba(209,250,229,0.22)",
                  textDecoration: "none",
                  transition: "color 0.15s",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.color = "rgba(209,250,229,0.55)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.color = "rgba(209,250,229,0.22)")
                }
              >
                {l}
              </Link>
            ))}
          </div>
          <p
            style={{ fontSize: 10, color: "rgba(209,250,229,0.2)", margin: 0 }}
          >
            © {year} ProteinSpot · Made with 🌿 in Bengaluru
          </p>
        </div>
      </div>

      <style>{`
        .ft-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 20px;
        }
        @media (min-width: 640px) {
          .ft-grid { grid-template-columns: 1fr 1fr; gap: 18px 32px; }
        }
        @media (min-width: 1024px) {
          .ft-grid { grid-template-columns: 1.5fr 0.8fr 0.7fr 1.3fr; gap: 36px; }
        }
      `}</style>
    </footer>
  );
};

export default memo(Footer);
